import { useEffect, useRef } from 'react'

interface Turnstile {
  render: (
    container: HTMLElement,
    options: {
      'sitekey': string
      'action': string
      'callback': (token: string) => void
      'error-callback': () => void
      'expired-callback': () => void
      'timeout-callback': () => void
    }
  ) => string
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: Turnstile
  }
}

let scriptPromise: Promise<Turnstile> | undefined

function loadTurnstile(): Promise<Turnstile> {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  scriptPromise ??= new Promise<Turnstile>((resolve, reject) => {
    const script = document.createElement('script')
    const fail = () => {
      script.remove()
      scriptPromise = undefined
      reject(new Error('Impossible de charger la vérification de sécurité. Veuillez réessayer.'))
    }
    const timeout = window.setTimeout(fail, 15000)
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.onload = () => {
      window.clearTimeout(timeout)
      if (window.turnstile) resolve(window.turnstile)
      else fail()
    }
    script.onerror = () => {
      window.clearTimeout(timeout)
      fail()
    }
    document.head.appendChild(script)
  })
  return scriptPromise
}

export function SmsCaptchaWidget({
  siteKey,
  onToken,
  onError,
}: {
  siteKey: string
  onToken: (token: string) => void
  onError: (error: Error) => void
}) {
  const container = useRef<HTMLDivElement>(null)
  useEffect(() => {
    let active = true
    let widgetId: string | undefined
    let turnstile: Turnstile | undefined
    const fail = () => {
      if (active) onError(new Error('La vérification de sécurité a échoué. Veuillez réessayer.'))
    }
    void loadTurnstile()
      .then((api) => {
        if (!active || !container.current) return
        turnstile = api
        widgetId = api.render(container.current, {
          'sitekey': siteKey,
          'action': 'sms_verification',
          'callback': (token) => {
            if (active) onToken(token)
          },
          'error-callback': fail,
          'expired-callback': fail,
          'timeout-callback': fail,
        })
      })
      .catch((error: unknown) => {
        if (active)
          onError(error instanceof Error ? error : new Error('Vérification indisponible.'))
      })
    return () => {
      active = false
      if (widgetId !== undefined) turnstile?.remove(widgetId)
    }
  }, [siteKey, onToken, onError])
  return <div ref={container} className="flex min-h-20 justify-center" />
}
